import { texts } from '@orgbrain/lib-data';
import {
  DividendEvent,
  Entity,
  ShareClass,
  ShareDeletionDataEvent,
  ShareholderEvent,
  ShareholderEventType,
  ShareholderWithStateEvent,
  ShareParvalueChangeEvent,
  SpliceEvent,
  SplitEvent,
  TransactionEvent,
} from '../../classes/shared';
import {
  translatedTypeCapitalized,
  standardHeader,
} from '../../imports/events/events-import-pipeline';
import { Entities } from '../../model/entity';

import moment from 'moment';

const translatedRoundingStrategy = {
  DOWN: {
    en: 'down',
    nb: 'ned',
  },
  UP: {
    en: 'up',
    nb: 'opp',
  },
};

export class EventsExportPipeline {
  static convert({
    entities,
    shareclasses,
    events,
  }: {
    entities: Entity[];
    shareclasses: ShareClass[];
    events: ShareholderEvent[];
  }): any[][] {
    const entityMap = entities.reduce(
      (
        entityMap: { [key: string]: Entity },
        entity: Entity
      ): {
        [key: string]: Entity;
      } => {
        entityMap[entity._id?.toString()] = entity;
        return entityMap;
      },
      {}
    );
    const shareclassMap = shareclasses.reduce(
      (
        shareclassMap: { [key: string]: ShareClass },
        shareclass: ShareClass
      ): { [key: string]: ShareClass } => {
        shareclassMap[shareclass._id?.toString()] = shareclass;
        return shareclassMap;
      },
      {}
    );
    // create headers
    const headers: string[] = Object.keys(standardHeader).map((key: string) => standardHeader[key]);
    // create list from objects
    const eventsArray: any[] = [headers];

    for (const event of events) {
      if (
        event.type === ShareholderEventType.import ||
        event.type === ShareholderEventType.emission ||
        event.type === ShareholderEventType.establish ||
        event.type === ShareholderEventType.edit
      ) {
        const eventWithState: ShareholderWithStateEvent = event as ShareholderWithStateEvent;
        for (const record of eventWithState.state.records) {
          const entity: Entity = entityMap[record.entityId];
          if (entity) {
            const shareclass: ShareClass = shareclassMap[record.shareclassId];
            const notes: string = record.notes ? '' + record.notes : '';

            const row = [
              translatedTypeCapitalized[event.type],
              this.formatDate(new Date(event.date || event.createdAt)),
              Entities.getEntityName(entity),
              Entities.getEntityEmail(entity),
              Entities.getEntityOrgnrOrSsn(entity),
              Entities.getOrgContactName(entity),
              '',
              '',
              '',
              record.amount,
              event.price || '',
              shareclass.name,
              (notes as string) || '',
              entity.address || '',
              entity.zip || '',
              entity.city || '',
              entity.tlf || '',
              entity.citizenship || '',
              entity.vps || '',
              entity.lei || '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
            ];
            eventsArray.push(row);
          }
        }
      } else if (event.type === ShareholderEventType.transaction) {
        const transaction: TransactionEvent = event as TransactionEvent;
        const buyer: Entity = entityMap[transaction.recipient];
        const seller: Entity = entityMap[transaction.sender];
        const shareclass: ShareClass = shareclassMap[transaction.shareclassId];

        const row: any[] = [
          texts[transaction.subType]['default']['nb'],
          this.formatDate(new Date(event.date || event.createdAt)),
          Entities.getEntityName(buyer),
          Entities.getEntityEmail(buyer),
          Entities.getEntityOrgnrOrSsn(buyer),
          Entities.getOrgContactName(buyer),
          Entities.getEntityName(seller),
          Entities.getEntityEmail(seller),
          Entities.getEntityOrgnrOrSsn(seller),
          transaction.amount,
          transaction.price || '',
          shareclass.name,
          '',
          buyer.address || '',
          buyer.zip || '',
          buyer.city || '',
          buyer.tlf || '',
          buyer.country || '',
          buyer.vps,
          buyer.lei,
          seller.address || '',
          seller.zip || '',
          seller.city || '',
          seller.country || '',
          seller.tlf || '',
          seller.vps || '',
          seller.lei || '',
          '',
          '',
          '',
          '',
        ];
        eventsArray.push(row);
      } else if (
        event.type === ShareholderEventType.split ||
        event.type === ShareholderEventType.splice
      ) {
        const sEvent: SplitEvent | SpliceEvent = event as SplitEvent | SpliceEvent;
        const shareclass: ShareClass = shareclassMap[sEvent.shareclassId];
        const row: any[] = [
          texts[event.type]['default']['nb'],
          this.formatDate(new Date(event.date || event.createdAt)),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          shareclass.name,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          sEvent.from,
          sEvent.to,
          translatedRoundingStrategy[sEvent.strategy]['nb'],
          '',
        ];
        eventsArray.push(row);
      } else if (event.type === ShareholderEventType.sharedelete) {
        const dEvent: ShareDeletionDataEvent = event as ShareDeletionDataEvent;
        const sender: Entity = entityMap[dEvent.sender];
        const shareclass: ShareClass = shareclassMap[dEvent.shareclassId];
        const row: any[] = [
          texts[event.type]['default']['nb'],
          this.formatDate(new Date(event.date || event.createdAt)),
          '',
          '',
          '',
          '',
          Entities.getEntityName(sender),
          Entities.getEntityEmail(sender),
          Entities.getEntityOrgnrOrSsn(sender),
          dEvent.amount,
          '',
          shareclass.name,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          sender.address || '',
          sender.zip || '',
          sender.city || '',
          sender.tlf || '',
          sender.citizenship || '',
          sender.vps || '',
          sender.lei || '',
          '',
          '',
          '',
          '',
        ];
        eventsArray.push(row);
      } else if (event.type === ShareholderEventType.dividend) {
        const dEvent: DividendEvent = event as DividendEvent;
        //const eventWithState: ShareholderWithStateEvent = event as ShareholderWithStateEvent;
        const shareclass: ShareClass = shareclassMap[dEvent.shareclassId];
        const row: any[] = [
          texts[event.type]['default']['nb'],
          this.formatDate(new Date(event.date || event.createdAt)),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          dEvent.dividend,
          shareclass.name,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          this.formatDate(dEvent.paymentDate),
        ];
        eventsArray.push(row);

        for (const record of dEvent.state.records) {
          const entity: Entity = entityMap[record.entityId];
          if (entity) {
            const notes: string = record.notes ? '' + record.notes : '';

            const row = [
              texts[event.type]['default']['nb'],
              this.formatDate(new Date(event.date || event.createdAt)),
              Entities.getEntityName(entity),
              Entities.getEntityEmail(entity),
              Entities.getEntityOrgnrOrSsn(entity),
              Entities.getOrgContactName(entity),
              '',
              '',
              '',
              record.amount,
              dEvent.dividend,
              shareclass.name,
              (notes as string) || '',
              entity.address || '',
              entity.zip || '',
              entity.city || '',
              entity.citizenship || '',
              entity.tlf || '',
              entity.vps || '',
              entity.lei || '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              this.formatDate(dEvent.paymentDate),
            ];
            eventsArray.push(row);
          }
        }
      } else if (event.type === ShareholderEventType.parvaluechange) {
        const parvaluechangeevent: ShareParvalueChangeEvent = event as ShareParvalueChangeEvent;

        const row = [
          texts[event.type]['default']['nb'],
          this.formatDate(new Date(event.date || event.createdAt)),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          parvaluechangeevent.parValue,
          shareclassMap[parvaluechangeevent.shareclassId].name,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ];
        eventsArray.push(row);
      }
    }

    return eventsArray;
  }

  private static formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    return moment(date).format('YYYY-MM-DD HH:mm');
  }
}
